import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { BookOpen, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function KnowledgeBase() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", tags: "" });
  const load = () => api.get("/kb", { params: q ? { q } : {} }).then(r => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  const submit = async () => {
    await api.post("/kb", { ...form, tags: form.tags.split(",").map(s => s.trim()).filter(Boolean) });
    toast.success("Article published");
    setOpen(false); setForm({ title:"", body:"", tags:"" }); load();
  };
  return (
    <div className="p-6" data-testid="kb-view">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Knowledge Base</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Articles</h1>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><Button data-testid="new-kb-btn" className="rounded-sm font-mono uppercase text-xs"><Plus size={14} className="mr-2" />New Article</Button></SheetTrigger>
          <SheetContent className="bg-background border-border w-full sm:max-w-lg">
            <SheetHeader><SheetTitle className="font-display">New KB Article</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-6">
              <div><Label className="text-xs uppercase">Title</Label><Input className="mt-1 rounded-sm" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} /></div>
              <div><Label className="text-xs uppercase">Body (markdown-ish)</Label><Textarea className="mt-1 rounded-sm min-h-[180px]" value={form.body} onChange={(e)=>setForm({...form,body:e.target.value})} /></div>
              <div><Label className="text-xs uppercase">Tags (comma-separated)</Label><Input className="mt-1 rounded-sm font-mono" value={form.tags} onChange={(e)=>setForm({...form,tags:e.target.value})} /></div>
              <Button onClick={submit} disabled={!form.title} className="w-full rounded-sm font-mono uppercase text-xs">Publish</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div className="relative max-w-md mb-4">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="kb-search" placeholder="Search articles…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 rounded-sm" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {items.map(a => (
          <Card key={a.id} className="p-5 border-border rounded-sm hover:border-primary/60 cursor-pointer" onClick={()=>setSelected(a)}>
            <div className="flex items-center gap-2 mb-2"><BookOpen size={14} className="text-primary" /><div className="font-display text-lg">{a.title}</div></div>
            <p className="text-sm text-muted-foreground line-clamp-3">{a.body}</p>
            <div className="mt-3 flex gap-1 flex-wrap">{a.tags?.map(t => <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 border border-border">{t}</span>)}</div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-muted-foreground">No articles found.</div>}
      </div>
      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={()=>setSelected(null)}>
          <Card className="max-w-2xl w-full p-8 border-border rounded-sm" onClick={(e)=>e.stopPropagation()}>
            <div className="font-display text-2xl mb-2">{selected.title}</div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-zinc-700 dark:text-zinc-200">{selected.body}</pre>
            <Button data-testid="kb-close" onClick={()=>setSelected(null)} variant="outline" className="mt-4 rounded-sm">Close</Button>
          </Card>
        </div>
      )}
    </div>
  );
}
